// src/pages/resources/CreateLongQuestionPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Grid, Card, CardContent, Paper, Button, TextField,
    Stack, Tabs, Tab, LinearProgress, InputAdornment, Chip,
    Snackbar, Alert, IconButton, Container, AppBar, Toolbar
} from '@mui/material';
import {
    ArrowBack,
    FormatListNumbered as NumberIcon,
    Image as ImageIcon,
    YouTube as YouTubeIcon,
    CloudUpload as UploadIcon,
    Link as LinkIcon
} from '@mui/icons-material';
import {
    createMultiMarkQuestion,
    updateMultiMarkQuestion
} from '../../services/chapterService';

export default function CreateLongQuestionPage() {
    const { id: chapterIdStr, marks: marksStr } = useParams<{ id: string; marks: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const chapterId = Number(chapterIdStr);
    const marks = Number(marksStr);
    const editQuestion = (location.state as any)?.editQuestion ?? null;
    const nextQuestionNumber = (location.state as any)?.nextQuestionNumber ?? 1;

    const isEdit = !!editQuestion;

    const [questionNumber, setQuestionNumber] = useState<string | number>('');
    const [question, setQuestion] = useState('');
    const [description, setDescription] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [videoMode, setVideoMode] = useState<'URL' | 'FILE'>('URL');
    const [videoUrl, setVideoUrl] = useState('');
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        if (isEdit) {
            setQuestionNumber(editQuestion.questionNumber || '');
            setQuestion(editQuestion.question || '');
            setDescription(editQuestion.description || '');
            const vid = editQuestion.resources?.find((r: any) => r.type === 'VIDEO');
            if (vid && vid.url) {
                setVideoUrl(vid.url);
                setVideoMode('URL');
            }
        } else {
            setQuestionNumber(nextQuestionNumber);
        }
    }, []);

    const showSnack = (msg: string, type: 'success' | 'error') => setSnackbar({ msg, type });

    const handleSave = async () => {
        if (!questionNumber) return showSnack('Question Number is required', 'error');
        if (!question.trim()) return showSnack('Question text is required', 'error');

        setSubmitting(true);
        try {
            const payload = {
                question,
                description,
                marks,
                questionNumber: Number(questionNumber),
                videoUrl: videoMode === 'URL' ? videoUrl : undefined
            };
            const fileToSend = videoMode === 'FILE' ? videoFile : null;

            if (isEdit) {
                await updateMultiMarkQuestion(chapterId, editQuestion.id, payload, imageFile, fileToSend);
            } else {
                await createMultiMarkQuestion(chapterId, payload, imageFile, fileToSend);
            }
            showSnack('Saved successfully!', 'success');
            setTimeout(() => navigate(-1), 800);
        } catch (e: any) {
            showSnack(e.message || 'Operation failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', pb: 12 }}>
            {/* Sticky AppBar Header */}
            <AppBar position="sticky" elevation={2} sx={{ bgcolor: '#174143' }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                            {isEdit ? 'Edit Question' : 'Add Question'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {marks} Mark Question — Chapter #{chapterId}
                        </Typography>
                    </Box>
                </Toolbar>
                {submitting && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
            </AppBar>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ pt: 4 }}>
                <Grid container spacing={4}>
                    {/* Left Column: Question Details */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Card sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
                                    textTransform="uppercase" mb={3}>
                                    Question Details
                                </Typography>

                                <Stack spacing={3}>
                                    {/* Row 1: Q. No + Marks */}
                                    <Stack direction="row" spacing={2}>
                                        <TextField
                                            label="Q. No"
                                            type="number"
                                            value={questionNumber}
                                            onChange={e => setQuestionNumber(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <NumberIcon fontSize="small" />
                                                    </InputAdornment>
                                                )
                                            }}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Marks"
                                            type="number"
                                            value={marks}
                                            disabled
                                            fullWidth
                                        />
                                    </Stack>

                                    {/* Question Text */}
                                    <TextField
                                        label="Question Text *"
                                        multiline
                                        rows={5}
                                        value={question}
                                        onChange={e => setQuestion(e.target.value)}
                                        placeholder="Enter the question here..."
                                        fullWidth
                                        required
                                    />

                                    {/* Answer Description */}
                                    <TextField
                                        label="Answer Description"
                                        multiline
                                        rows={3}
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        placeholder="Optional answer key or notes..."
                                        fullWidth
                                    />

                                    {/* Image Upload */}
                                    <Box
                                        sx={{
                                            p: 2.5,
                                            border: '1px dashed #ccc',
                                            borderRadius: 2,
                                            bgcolor: '#f8fdf8'
                                        }}
                                    >
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                            Question Image (Optional)
                                        </Typography>
                                        <Stack direction="row" alignItems="center" gap={2}>
                                            <Button
                                                component="label"
                                                size="small"
                                                variant="outlined"
                                                startIcon={<ImageIcon />}
                                            >
                                                {imageFile ? 'Change' : 'Select Image'}
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={e => setImageFile(e.target.files?.[0] || null)}
                                                />
                                            </Button>
                                            {imageFile && (
                                                <Chip
                                                    label={imageFile.name}
                                                    onDelete={() => setImageFile(null)}
                                                    size="small"
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Column: Video Section */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Card sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
                                    textTransform="uppercase" mb={3}>
                                    Explanation Video
                                </Typography>

                                <Tabs
                                    value={videoMode}
                                    onChange={(_, v) => setVideoMode(v)}
                                    variant="fullWidth"
                                    sx={{ mb: 3, bgcolor: '#f4f6f8', borderRadius: 1 }}
                                >
                                    <Tab
                                        label="URL"
                                        value="URL"
                                        icon={<YouTubeIcon fontSize="small" />}
                                        iconPosition="start"
                                    />
                                    <Tab
                                        label="UPLOAD"
                                        value="FILE"
                                        icon={<UploadIcon fontSize="small" />}
                                        iconPosition="start"
                                    />
                                </Tabs>

                                {videoMode === 'URL' ? (
                                    <TextField
                                        fullWidth
                                        label="Video URL"
                                        placeholder="https://youtube.com/..."
                                        value={videoUrl}
                                        onChange={e => setVideoUrl(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LinkIcon fontSize="small" />
                                                </InputAdornment>
                                            )
                                        }}
                                        helperText="Paste a direct video link or YouTube URL."
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            py: 5,
                                            border: '2px dashed #e0e0e0',
                                            borderRadius: 2,
                                            bgcolor: '#fff',
                                            cursor: 'pointer',
                                            '&:hover': { borderColor: '#174143', bgcolor: '#f0fcfc' }
                                        }}
                                        onClick={() => document.getElementById('video-upload-input')?.click()}
                                    >
                                        <input
                                            id="video-upload-input"
                                            type="file"
                                            hidden
                                            accept="video/*"
                                            onChange={e => setVideoFile(e.target.files?.[0] || null)}
                                        />
                                        <UploadIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                                        <Typography fontWeight={600} color="text.primary">
                                            {videoFile ? videoFile.name : 'Click to Upload Video'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                            MP4, WebM supported
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>

            {/* Sticky Bottom Action Bar */}
            <Paper
                elevation={4}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    px: 4,
                    py: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    bgcolor: '#fff',
                    zIndex: 1200
                }}
            >
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={submitting}
                    sx={{ px: 4, bgcolor: '#174143', '&:hover': { bgcolor: '#0f2d2f' } }}
                >
                    {submitting ? 'Saving...' : 'Save Question'}
                </Button>
            </Paper>

            <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)}>
                <Alert severity={snackbar?.type}>{snackbar?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
